import { InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

import type { GridPageInfo } from './grid-page-info.interface';
import type { GridQueryFilter } from './grid-query-filter.type';
import type { GridRow } from './grid-row.type';

/**
 * Backend attribute-filter operator values.
 * Matches GraphQL `AttributeFilterGraphOperator` / `AttributeFilterInput.operator`.
 */
export type GridBackendAttributeFilterOperator =
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'STARTS_WITH'
  | 'NOT_STARTS_WITH'
  | 'ENDS_WITH'
  | 'NOT_ENDS_WITH'
  | 'IS_NULL'
  | 'IS_NOT_NULL';

/**
 * Optional JSONB cast hint for backend attribute filters.
 * Matches GraphQL `AttributeFilterInput.type`.
 */
export type GridBackendAttributeFilterType = 'TEXT' | 'INTEGER' | 'DECIMAL' | 'DATE';

/**
 * Backend attribute filter entry passed through to the data source.
 * Shape matches GraphQL `AttributeFilterInput` so host adapters do not need to recast.
 */
export interface GridBackendAttributeFilter {
  /** JSONB key name (e.g. `B_W_01_YEAR_BUILT`). */
  readonly name: string;
  /** Backend operator enum value. */
  readonly operator: GridBackendAttributeFilterOperator;
  /** Comparison value (always a string; the backend casts via `type`). */
  readonly value: string;
  /** Optional type hint for JSONB value casting. */
  readonly type?: GridBackendAttributeFilterType;
}

/** Opaque backend sort order passed through to the data source. */
export type GridBackendSortOrder = Record<string, unknown>;

/** Opaque backend attribute sort passed through to the data source. */
export type GridBackendAttributeSort = Record<string, unknown>;

/** Unified sort field passed through to the data source. */
export interface GridSortField {
  /** Column or attribute field name to sort by. */
  readonly field: string;
  /** Sort direction understood by the backend. */
  readonly direction: 'ASC' | 'DESC';
}

/** Request for a flat or grouped row page. */
export interface GridRowsRequest {
  /** Page size for forward pagination. */
  readonly first?: number;
  /** Cursor to continue forward pagination. */
  readonly after?: string;
  /** Neutral where-clause filter. */
  readonly where?: GridQueryFilter;
  /** Unified sort fields passed through to the data source. */
  readonly sort?: ReadonlyArray<GridSortField>;
  /** Opaque backend sort order. */
  readonly order?: GridBackendSortOrder;
  /** Opaque backend attribute sort. */
  readonly attributeSort?: GridBackendAttributeSort;
  /** Opaque backend attribute filters. */
  readonly attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
}

/** Paginated row response. */
export interface GridRowsPage {
  readonly totalCount: number;
  readonly nodes: ReadonlyArray<GridRow>;
  readonly pageInfo?: GridPageInfo;
}

/** Request for a recursive group summary tree. */
export interface GridGroupTreeRequest {
  readonly fields: ReadonlyArray<string>;
  readonly scopes: ReadonlyArray<string>;
  readonly where?: GridQueryFilter;
  readonly attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
  readonly first?: number;
  readonly after?: string;
}

/** A leaf path and count from a group summary tree. */
export interface GridGroupTreeNode {
  readonly path: string;
  readonly count: number;
}

/** Recursive group summary tree response. */
export interface GridGroupTreePage {
  readonly nodes: ReadonlyArray<GridGroupTreeNode>;
  readonly totalPathCount: number;
  readonly truncated: boolean;
  readonly pageInfo: GridPageInfo;
}

/** Request for a paginated primary group summary. */
export interface GridGroupSummaryRequest {
  readonly field: string;
  readonly scopes: ReadonlyArray<string>;
  readonly where?: GridQueryFilter;
  readonly attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
  readonly first?: number;
  readonly after?: string;
}

/** A single group value and count. */
export interface GridGroupSummaryItem {
  readonly value: string;
  readonly count: number;
}

/** Paginated group summary response. */
export interface GridGroupSummaryPage {
  readonly totalDistinctCount: number;
  readonly nodes: ReadonlyArray<GridGroupSummaryItem>;
  readonly pageInfo: GridPageInfo;
}

/** Input describing one bucket scope for a batch row fetch. */
export interface GridBucketScope {
  readonly bucketKey: string;
  readonly where: GridQueryFilter;
  readonly attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
}

/** Request for batched first-page bucket row loads. */
export interface GridBucketRowsRequest {
  readonly buckets: ReadonlyArray<GridBucketScope>;
  readonly sharedWhere: GridQueryFilter;
  readonly attributeFilter?: ReadonlyArray<GridBackendAttributeFilter>;
  readonly sort?: ReadonlyArray<GridSortField>;
  readonly order?: GridBackendSortOrder;
  readonly attributeSort?: GridBackendAttributeSort;
  readonly firstPerBucket?: number;
}

/** Row payload for one bucket in a batch response. */
export interface GridBucketRowsPage {
  readonly bucketKey: string;
  readonly totalCount: number;
  readonly nodes: ReadonlyArray<GridRow>;
  readonly pageInfo: GridPageInfo;
}

/**
 * Host-provided data access for the grid orchestrator.
 * Implementations adapt domain APIs (GraphQL, REST, etc.) to neutral grid rows.
 */
export interface GridDataSource {
  fetchRows(request: GridRowsRequest): Observable<GridRowsPage>;
  fetchGroupTree(request: GridGroupTreeRequest): Observable<GridGroupTreePage>;
  fetchGroupSummary(request: GridGroupSummaryRequest): Observable<GridGroupSummaryPage>;
  /** Non-paginated fallback used when paginated group summary fails. */
  fetchGroupSummaryList(
    request: Omit<GridGroupSummaryRequest, 'first' | 'after'>
  ): Observable<ReadonlyArray<GridGroupSummaryItem>>;
  fetchBucketRows(request: GridBucketRowsRequest): Observable<ReadonlyArray<GridBucketRowsPage>>;
}

export const GRID_DATA_SOURCE: InjectionToken<GridDataSource> = new InjectionToken<GridDataSource>(
  'GRID_DATA_SOURCE'
);
